import { useConnectionStatus } from '@platewell/query';

interface StatusLabel {
  text: string;
  tone: 'online' | 'offline' | 'error';
  hint: string;
}

const OFFLINE: StatusLabel = {
  text: 'Offline',
  tone: 'offline',
  hint: 'Edits are saved here and sync later.',
};

const LABELS: Record<string, StatusLabel> = {
  synced: { text: 'Synced', tone: 'online', hint: 'Everything is saved and shared.' },
  connected: { text: 'Syncing', tone: 'online', hint: 'Catching up with the household.' },
  connecting: { text: 'Connecting', tone: 'offline', hint: 'Reaching the household…' },
  offline: OFFLINE,
  read_only: { text: 'View only', tone: 'online', hint: 'You can read but not change recipes.' },
  error: { text: 'Sync problem', tone: 'error', hint: 'Retrying automatically.' },
  subscription_lapsed: {
    text: 'Locked',
    tone: 'error',
    hint: 'Update the payment method to sync again. Nothing has been deleted.',
  },
};

/**
 * Offline is a normal state here, not an error, so it reads as a neutral status rather than
 * a warning. Nothing is ever lost by being offline; the wording should not suggest it is.
 *
 * The label is wrapped rather than left as a bare text node so the narrow top bar can hide
 * it and keep just the dot. It is hidden visually, not removed, so the pill still reads as
 * "Synced" to a screen reader at every width.
 */
export function SyncStatus() {
  const { status } = useConnectionStatus();
  const label = LABELS[status] ?? OFFLINE;

  return (
    <span className={`status ${label.tone}`} title={label.hint}>
      <span className="dot" />
      <span className="status-label">{label.text}</span>
    </span>
  );
}
